import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { CheckCircle2, ExternalLink, Sparkles, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public portal — magic-link authenticated. No login required.
export default function ClientPortal() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [error, setError] = useState(null);
  const viewedRef = useRef(new Set());

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/portal/session/${token}`);
      setSession(data);
    } catch (err) {
      setError(err.response?.data?.detail || "This portal link is invalid or expired. Please ask your Clients OnRamp contact for a new one.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  // Analytics: fire a `view` event for each add-on card that gets rendered
  // above the fold (or lazily once catalog loads). Debounced via a ref set
  // so we only report each addon_id once per portal session.
  useEffect(() => {
    if (!session?.catalog) return;
    const activeIds = new Set((session.active_addons || []).map((a) => a.addon_id));
    const visible = session.catalog.filter((a) => !activeIds.has(a.id));
    visible.forEach((a) => {
      if (viewedRef.current.has(a.id)) return;
      viewedRef.current.add(a.id);
      axios.post(`${API}/portal/session/${token}/track`, { event: "view", addon_id: a.id }).catch(() => {});
    });
  }, [session, token]);

  // If the URL contains ?addon_activated=..., re-fetch the session (webhook
  // may have updated in the background) and show a success toast.
  useEffect(() => {
    const activated = searchParams.get("addon_activated");
    const cancelled = searchParams.get("addon_cancelled");
    if (activated) {
      toast.success("Payment received — your add-on will appear here in a moment.");
      // small delay to let the Stripe webhook complete
      setTimeout(() => load(), 2500);
      searchParams.delete("addon_activated");
      searchParams.delete("session_id");
      setSearchParams(searchParams, { replace: true });
    } else if (cancelled) {
      axios.post(`${API}/portal/session/${token}/track`, { event: "dismiss", addon_id: cancelled }).catch(() => {});
      toast.info("Checkout cancelled — no charge was made.");
      searchParams.delete("addon_cancelled");
      setSearchParams(searchParams, { replace: true });
    }
    /* eslint-disable-next-line */
  }, [searchParams]);

  const startCheckout = async (addonId, name) => {
    setBusy((b) => ({ ...b, [addonId]: true }));
    try {
      const { data } = await axios.post(
        `${API}/portal/session/${token}/addons/${addonId}/checkout`,
        { origin_url: window.location.origin }
      );
      if (data.status === "already_active") {
        toast.info(`${name} is already active on your account.`);
        return;
      }
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error("Could not start checkout — please contact support.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally {
      setBusy((b) => ({ ...b, [addonId]: false }));
    }
  };

  const activeIds = useMemo(
    () => new Set((session?.active_addons || []).map((a) => a.addon_id)),
    [session]
  );

  const activeMrr = useMemo(
    () => (session?.active_addons || []).reduce((s, a) => s + (a.price || 0), 0),
    [session]
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F4EE]">
        <Loader2 className="w-8 h-8 animate-spin text-[#E24E1B]" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F4EE] p-8">
        <div className="max-w-md text-center">
          <h1 className="font-display text-3xl font-bold mb-4">Link expired</h1>
          <p className="text-neutral-600">{error}</p>
        </div>
      </div>
    );
  }
  const { client, catalog } = session;

  return (
    <div className="min-h-screen bg-[#F7F4EE]" data-testid="client-portal-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="bg-[#0F2340] text-white py-8 px-6">
        <div className="max-w-5xl mx-auto flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#E24E1B] font-semibold mb-2">Your Clients OnRamp Portal</div>
            <h1 className="font-display text-3xl font-bold">{client.business_name || "Your account"}</h1>
            <div className="text-sm text-neutral-300 mt-1">
              {[client.industry, client.city, client.state].filter(Boolean).join(" · ")}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-neutral-300 uppercase tracking-widest">Current add-on MRR</div>
            <div className="font-display text-3xl font-bold text-[#E24E1B]">
              ${activeMrr}<span className="text-sm text-neutral-300 font-mono ml-1">/mo</span>
            </div>
          </div>
        </div>
      </header>

      {/* Active add-ons */}
      {activeIds.size > 0 && (
        <section className="max-w-5xl mx-auto px-6 pt-8">
          <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Your active features
          </h2>
          <div className="grid md:grid-cols-3 gap-3">
            {session.active_addons.map((a) => (
              <div key={a.addon_id} className="bg-white border-l-4 border-emerald-500 p-4">
                <div className="font-semibold">{a.addon_name}</div>
                <div className="text-xs text-neutral-500 font-mono">${a.price}/mo · activated {new Date(a.activated_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Available add-ons */}
      <section className="max-w-5xl mx-auto px-6 py-10">
        <h2 className="font-display text-2xl font-bold mb-2 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-[#E24E1B]" /> Boost your account
        </h2>
        <p className="text-neutral-600 mb-6">
          Add features to your plan securely via Stripe. Prices are monthly on top of your current plan.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(catalog || []).filter((a) => !activeIds.has(a.id)).map((a) => (
            <div key={a.id} data-testid={`portal-addon-${a.id}`}
                 onMouseEnter={() => {
                   if (!viewedRef.current.has(`hover:${a.id}`)) {
                     viewedRef.current.add(`hover:${a.id}`);
                     axios.post(`${API}/portal/session/${token}/track`, { event: "hover", addon_id: a.id }).catch(() => {});
                   }
                 }}
                 className="bg-white border border-neutral-200 p-5 flex flex-col hover:border-[#E24E1B] transition">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">Tier {a.tier}</span>
                <span className="font-mono text-sm font-semibold">
                  ${a.price}/mo{a.setup_fee > 0 && <span className="text-neutral-400 text-xs"> · ${a.setup_fee} setup</span>}
                </span>
              </div>
              <div className="font-semibold mb-1">{a.name}</div>
              <p className="text-sm text-neutral-600 leading-relaxed mb-4 flex-1">{a.tagline}</p>
              <button onClick={() => startCheckout(a.id, a.name)} disabled={busy[a.id]}
                      data-testid={`portal-checkout-${a.id}`}
                      className="w-full bg-[#0F2340] text-white text-sm font-semibold py-2 hover:bg-[#E24E1B] transition flex items-center justify-center gap-1">
                <ExternalLink className="w-4 h-4" /> {busy[a.id] ? "Redirecting…" : "Add via Stripe"}
              </button>
            </div>
          ))}
          {(catalog || []).filter((a) => !activeIds.has(a.id)).length === 0 && (
            <div className="col-span-full text-center py-10 text-neutral-500">
              🎉 You have every add-on activated. Talk to your account rep about custom services.
            </div>
          )}
        </div>
      </section>

      <footer className="max-w-5xl mx-auto px-6 py-6 border-t border-neutral-200 text-xs text-neutral-500 flex justify-between">
        <span>© Clients OnRamp · Secure portal link · Do not share</span>
        <a href="tel:+15625743752" className="text-[#0F2340] hover:underline">Questions? (562) 574-3752</a>
      </footer>
    </div>
  );
}
